import React, { useCallback, useEffect, useState } from "react";
import { Boxes, FileDown, Plus, Trash2, ShieldAlert, CheckCircle2, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const RAG = { green: "#16803c", amber: "#b45309", red: "#b91c1c", grey: "#a1a1aa" };
const TIER_COL = { 1: "#b91c1c", 2: "#b45309", 3: "#a16207", 4: "#4d7c0f", 5: "#71717a" };
const BLANK = { name: "", purpose: "", owner: "", challenger: "", sponsor: "", model_class: "supervised",
  technique: "", vendor: false, status: "In development", regulatory_scope: "", dimensions: {} };

function downloadBlob(res, name) {
  const url = URL.createObjectURL(res.data); const a = document.createElement("a");
  a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
}

export default function ModelRegister({ clientId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/mrm-register`);
      setData(data);
    } catch { toast.error("Could not load model register"); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const cat = data?.catalogue;
  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/enterprise360/clients/${clientId}/mrm-register`, form);
      toast.success("Model registered"); setForm(null); load();
    } catch { toast.error("Could not register model"); } finally { setBusy(false); }
  };
  const toggleGate = async (m, gid, done) => {
    try {
      await api.put(`/admin/enterprise360/clients/${clientId}/mrm-register/${m.id}`,
        { gates: { [gid]: { done, approver: done ? "admin" : null } } });
      load();
    } catch { toast.error("Could not update gate"); }
  };
  const toggleBreach = async (m) => {
    try { await api.put(`/admin/enterprise360/clients/${clientId}/mrm-register/${m.id}`, { monitoring_breach: !m.monitoring_breach }); load(); }
    catch { toast.error("Could not update"); }
  };
  const remove = async (m) => {
    try { await api.delete(`/admin/enterprise360/clients/${clientId}/mrm-register/${m.id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };
  const pdf = async () => {
    try { downloadBlob(await api.get(`/admin/enterprise360/clients/${clientId}/mrm-register.pdf`, { responseType: "blob" }), "Model_Risk_Register.pdf"); }
    catch { toast.error("Could not download"); }
  };

  if (!data) return <div className="text-sm ek-text-muted py-4">Loading model register…</div>;
  const p = data.posture;

  return (
    <div data-testid="model-register">
      <p className="text-[12px] ek-text-muted max-w-2xl mb-4">
        <Boxes size={12} className="inline mr-1" /> Client models as risk objects (MRM Framework v1.0). Tier is set by the
        highest of seven impact dimensions; controls scale with tier; seven gates G1–G7 govern the lifecycle. G5 registers the model as an ERIO risk object.
      </p>

      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        <div className="lg:col-span-2 border rounded-lg p-4" style={{ borderColor: (RAG[p.rag] || "#e4e4e7") + "66", background: (RAG[p.rag] || "#a1a1aa") + "0d" }} data-testid="mrm-posture">
          <div className="text-[10px] uppercase tracking-[0.12em] ek-text-muted">Board Risk Posture Score</div>
          <div className="flex items-baseline gap-3">
            <span className="text-4xl font-bold" style={{ color: RAG[p.rag] }} data-testid="mrm-posture-score">{p.score ?? "—"}<span className="text-lg ek-text-muted">/100</span></span>
            <span className="text-[13px] font-semibold uppercase" style={{ color: RAG[p.rag] }}>{p.rag}</span>
            <span className="text-[12px] ek-text-muted">{p.total_models} model(s) · {p.tier1} Tier-1 · {p.breaches || 0} breach(es)</span>
          </div>
          <ul className="mt-2 space-y-0.5">
            {(p.drivers || []).slice(0, 5).map((d, i) => <li key={i} className="text-[11.5px] ek-text-muted">• {d}</li>)}
          </ul>
        </div>
        <div className="flex flex-col gap-2 justify-center">
          <button data-testid="mrm-add-model" onClick={() => setForm({ ...BLANK, dimensions: {} })} className="inline-flex items-center justify-center gap-1.5 text-[12px] tracking-[0.1em] uppercase text-white bg-[var(--e360-primary)] px-3 py-2 rounded hover:opacity-90"><Plus size={13} /> Register a model</button>
          <button data-testid="mrm-register-pdf" onClick={pdf} className="inline-flex items-center justify-center gap-1.5 text-[12px] tracking-[0.1em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-2 rounded hover:bg-[var(--e360-primary)]/5"><FileDown size={13} /> Board pack PDF</button>
        </div>
      </div>

      <div className="space-y-3">
        {data.models.length === 0 && <p className="text-[13px] ek-text-muted">No models registered yet.</p>}
        {data.models.map((m) => (
          <div key={m.id} data-testid={`mrm-model-${m.id}`} className="border ek-line rounded-lg p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] font-semibold ek-text">{m.name}</span>
                <span className="text-[10.5px] font-bold px-2 py-0.5 rounded text-white" style={{ background: TIER_COL[m.tier] }}>TIER {m.tier} {m.tier_name}</span>
                <span className="text-[11px] ek-text-muted">{cat.model_classes[m.model_class]}</span>
                {m.vendor && <span className="text-[10px] px-1.5 py-0.5 rounded ek-surface-2 ek-text-muted">VENDOR</span>}
                {m.card_complete
                  ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 size={12} /> Card complete</span>
                  : <span className="inline-flex items-center gap-1 text-[11px] text-amber-700"><ShieldAlert size={12} /> Card incomplete</span>}
              </div>
              <button onClick={() => remove(m)} data-testid={`mrm-del-${m.id}`} className="ek-text-muted hover:text-red-600"><Trash2 size={14} /></button>
            </div>
            <div className="text-[11px] ek-text-muted mb-2">Owner {m.owner || "—"} · Challenger {m.challenger || "—"} · Sponsor {m.sponsor || "—"} · Monitoring {m.monitoring_cadence}</div>
            <div className="flex gap-1 flex-wrap items-center">
              {cat.gates.map((g, i) => {
                const done = (m.gates?.[g.id] || {}).done;
                const reachable = i <= m.gate_position.passed;
                return (
                  <button key={g.id} data-testid={`mrm-gate-${m.id}-${g.id}`} disabled={!reachable}
                    onClick={() => toggleGate(m, g.id, !done)} title={`${g.name} · ${g.accountable}`}
                    className={`text-[11px] px-2 py-1 rounded border transition-colors ${done ? "bg-[var(--e360-primary)] text-white border-[var(--e360-primary)]" : reachable ? "ek-line ek-text-muted hover:bg-zinc-50" : "ek-line ek-text-muted cursor-not-allowed"}`}>
                    {g.id}
                  </button>
                );
              })}
              <span className="text-[11px] ek-text-muted ml-1">{m.gate_position.passed}/7 · {m.gate_position.current}</span>
              <label className="ml-3 flex items-center gap-1 text-[11px] ek-text-muted">
                <input type="checkbox" data-testid={`mrm-breach-${m.id}`} checked={!!m.monitoring_breach} onChange={() => toggleBreach(m)} /> monitoring breach
              </label>
            </div>
            {!m.card_complete && <ul className="mt-2 text-[11px] text-amber-700 space-y-0.5">{m.card_blocking_reasons.map((r, i) => <li key={i}>— {r}</li>)}</ul>}
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setForm(null)}>
          <div className="ek-surface rounded-lg max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mrm-form">
            <button onClick={() => setForm(null)} className="absolute top-4 right-4 ek-text-muted hover:text-zinc-700"><X size={18} /></button>
            <h3 className="ek-heading text-lg mb-3">Register a model</h3>
            <div className="space-y-2.5">
              <input data-testid="mrm-f-name" placeholder="Model name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border ek-line rounded px-2.5 py-1.5 text-[13px]" />
              <div className="grid grid-cols-3 gap-2">
                <input data-testid="mrm-f-owner" placeholder="Owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="border ek-line rounded px-2.5 py-1.5 text-[13px]" />
                <input data-testid="mrm-f-challenger" placeholder="Challenger" value={form.challenger} onChange={(e) => setForm({ ...form, challenger: e.target.value })} className="border ek-line rounded px-2.5 py-1.5 text-[13px]" />
                <input data-testid="mrm-f-sponsor" placeholder="Sponsor" value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })} className="border ek-line rounded px-2.5 py-1.5 text-[13px]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select data-testid="mrm-f-class" value={form.model_class} onChange={(e) => setForm({ ...form, model_class: e.target.value })} className="border ek-line rounded px-2 py-1.5 text-[13px]">
                  {Object.entries(cat.model_classes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <label className="flex items-center gap-2 text-[12px] ek-text-muted"><input type="checkbox" checked={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.checked })} /> Third-party / vendor</label>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.12em] ek-text-muted mb-1.5">Tiering — score each dimension 1 (low) → 5 (critical)</div>
                {cat.dimensions.map((d) => (
                  <div key={d.key} className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[12px] ek-text-muted flex-1">{d.label}</span>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button" data-testid={`mrm-f-dim-${d.key}-${n}`}
                          onClick={() => setForm({ ...form, dimensions: { ...form.dimensions, [d.key]: n } })}
                          className={`w-6 h-6 rounded text-[11px] border ${form.dimensions[d.key] === n ? "bg-[var(--e360-primary)] text-white border-[var(--e360-primary)]" : "ek-line ek-text-muted"}`}>{n}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button data-testid="mrm-f-submit" onClick={create} disabled={busy || !form.name} className="w-full inline-flex items-center justify-center gap-1.5 text-[13px] uppercase tracking-[0.1em] text-white bg-[var(--e360-primary)] px-3 py-2 rounded hover:opacity-90 disabled:opacity-40">{busy ? "Registering…" : "Register model"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
